/**
 * ConnectorsPageV2 — proper v2 redesign of Connectors (G110).
 *
 * Canonical inventory pattern:
 *   FacetRail (Type / Tier / Status / Category)
 *     + v2 Table (icon · name · type · tier · auth · status · actions)
 *     + Slideout (Overview · Auth · Skills offered · Manifest · Audit)
 *
 * Reuses every backend code path from v1 ConnectorsPage:
 *   - buildMarketplaceFromRegistry, findMarketplaceForInstalled
 *   - getConnectorConfig, saveConnectorConfig, authorizeConnector
 *   - getMyConnections, listConnectorManifests, listConnectorCategories
 *   - usePlatform() for connectors / loadConnectors / createConnector
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthBuildSectionEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import { buildMarketplaceFromRegistry, findMarketplaceForInstalled } from '../data/marketplaceFromRegistry';
import { listConnectorManifests, listConnectorCategories } from '../services/api';
import {
  RefreshCw, Plus, Plug, ShoppingBag, Layers, BarChart3, CheckCircle,
  XCircle, ExternalLink, Edit3, Trash2, Settings, Eye, Users, Zap,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TabNav, TenantScopePicker, ConnectorAssignTenantsSlideout,
  ConnectorOwnerPicker,
} from '../components/v2';
import { ConnectorInstallModalV2 } from '../components/v2/ConnectorInstallModalV2';
import { ConnectorConfigModalV2 } from '../components/v2/ConnectorConfigModalV2';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import { deleteConnector } from '../services/api';

import ConnectorsPage from './ConnectorsPage';

/* G173 — Status facet exposes the actual health/activity state from
 * connector.status (the same field V1 displays). For installed connectors the
 * platform stores: connected, active, pending, error, inactive, disabled.
 * Uninstalled rows surface as 'available'. */
const STATUS_OPTIONS = [
  { key: 'connected', label: 'Connected', tone: 'success' },
  { key: 'pending',   label: 'Pending',   tone: 'warning' },
  { key: 'error',     label: 'Error',     tone: 'danger'  },
  { key: 'inactive',  label: 'Inactive',  tone: 'muted'   },
  { key: 'disabled',  label: 'Disabled',  tone: 'muted'   },
  { key: 'available', label: 'Available', tone: 'muted'   },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.label]));

/* RC-74.3 — Governance badge tone maps + helpers.
 *
 * The connector marketplace manifest (see data/marketplaceFromRegistry.js
 * lines 269-270, 357-358) carries `guardrails[]` and `evals[]` arrays
 * that the V2 redesign dropped from the detail slideout — operators
 * lost the same pre-install governance signal that RC-71 restored for
 * agents. We mirror the agent-side mapping verbatim so a connector
 * row's "critical" guardrail renders with the same danger tone as an
 * agent's, and "functional / negative / guardrail / security / cost"
 * eval pills look identical across both surfaces. Lookups are tolerant
 * — unknown enum values fall back to muted so the badge always renders. */
const SEVERITY_TONE = {
  critical: 'danger',
  high:     'warning',
  medium:   'brand',
  low:      'cyan',
};

const EVAL_TYPE_TONE = {
  functional: 'action',
  negative:   'warning',
  guardrail:  'rose',
  security:   'danger',
  cost:       'success',
};

function SeverityBadge({ severity }) {
  if (!severity) return null;
  return <Pill tone={SEVERITY_TONE[severity] || 'muted'} size="sm">{severity}</Pill>;
}

function EvalTypeBadge({ type }) {
  if (!type) return null;
  return <Pill tone={EVAL_TYPE_TONE[type] || 'muted'} size="sm">{type}</Pill>;
}

/* Single source of truth: turn a row's raw record into one of the facet keys.
 * Reads connector.status (live health from the platform), falls back to the
 * lastTestResult enum for older records, then to install state. Aliases like
 * "active" map onto "connected" so the facet count is consistent. */
function rowHealthState(r) {
  if (!r.installed) return 'available';
  const inst = r.instance || r;
  const raw = (inst.status || inst.healthStatus || '').toString().toLowerCase();
  if (raw === 'connected' || raw === 'active' || raw === 'healthy') return 'connected';
  if (raw === 'pending'   || raw === 'pending_verification')        return 'pending';
  if (raw === 'error'     || raw === 'failed' || raw === 'unhealthy') return 'error';
  if (raw === 'inactive')  return 'inactive';
  if (raw === 'disabled')  return 'disabled';
  /* Last-resort fallback: if last test passed without a current status field. */
  const tr = (inst.lastTestResult || '').toString().toLowerCase();
  if (tr === 'success') return 'connected';
  if (tr === 'failed' || tr === 'error') return 'error';
  return 'inactive';
}

function statusLabelFor(s) {
  return STATUS_LABEL[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown');
}

function ConnectorsPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const platform = usePlatform();
  const { connectors = [], loadConnectors, createConnector } = platform;
  const auth = useAuth();
  const orgId = auth?.session?.orgId;
  const tenantId = auth?.session?.tenantId;
  /* V226 / CONN-SCOPE — roles allowed to grant cross-tenant scope. Mirrors
   * ConnectorService.canGrantScope() on the BE; FE hides the picker for
   * Tenant Admin so we don't surface a field the BE would 403 on. */
  const canGrantScope = ['Org Admin', 'Super Admin', 'Platform Admin'].includes(auth?.session?.role);

  /* V226 / CONN-SCOPE — row-level Assign-to-tenants slideout state. */
  const [assignTenantsRow, setAssignTenantsRow] = useState(null);

  /* G207 — V2-native Add Connector modal. Calls platform.createConnector
   * (POST /api/v1/connectors). Mirrors v1 free-form payload shape: name,
   * description, type, url, authMethod, authHeader, apiKey. */
  const ADD_DEFAULT = {
    name: '', description: '', type: 'REST',
    url: '', authMethod: 'none', authHeader: '', apiKey: '',
    /* CONN-OWNER — explicit owner Org + Tenant for the new connector. */
    ownerOrgId: '', ownerTenantId: '',
    /* V226 + V227 / CONN-SCOPE — additional tenants to share on create.
     * Granular shape: [{tenantId, instanceIds[]}] — empty instanceIds means
     * cascade. Org/Super/Platform Admin only; BE rejects from Tenant Admin
     * with 403 (FE hides the picker so we never surface a doomed field). */
    scopeGrants: [],
  };
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(ADD_DEFAULT);
  /* RC-74.6 — Test URL probe state. Scoped to the Add Connector
   * slideout; resets every time the user edits the URL field so a
   * stale "Reachable" tick can't survive a typo. */
  const [urlTesting, setUrlTesting] = useState(false);
  const [urlTestResult, setUrlTestResult] = useState(null);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const openAdd = () => {
    setAddForm(ADD_DEFAULT);
    setAddError(null);
    setAddOpen(true);
  };
  const submitAdd = async () => {
    setAddError(null);
    if (!addForm.name.trim()) { setAddError('Name is required.'); return; }
    if (!addForm.url.trim()) { setAddError('URL is required.'); return; }
    /* CONN-OWNER — owner Org + Tenant required. Tenant Admin's picker
     * auto-fills before submit; higher roles must pick explicitly. */
    if (!addForm.ownerOrgId || !addForm.ownerTenantId) {
      setAddError('Pick the Org + Tenant that will own this connector before saving.');
      return;
    }
    setAddSaving(true);
    try {
      const payload = {
        name: addForm.name.trim(),
        description: addForm.description.trim() || null,
        type: addForm.type,
        url: addForm.url.trim(),
        authMethod: addForm.authMethod,
        authHeader: addForm.authHeader || undefined,
        apiKey: addForm.apiKey || undefined,
        /* CONN-OWNER — explicit owner overrides session defaults. The
         * tenantId/orgId fields are kept for backward-compat with any
         * BE branch that still reads them, set from the explicit pick. */
        tenantId: addForm.ownerTenantId,
        orgId: addForm.ownerOrgId,
        ownerOrgId: addForm.ownerOrgId,
        ownerTenantId: addForm.ownerTenantId,
      };
      /* V226 + V227 / CONN-SCOPE — two-step share at create. The create
       * payload only carries cascade-tenant grants via the transient
       * scopeTenantIds field (BE accepts it natively); per-instance
       * grants are applied via a follow-up assignTenantGrants call on
       * the new connector's id (see post-success block below). */
      if (canGrantScope && Array.isArray(addForm.scopeGrants) && addForm.scopeGrants.length > 0) {
        const cascadeTenants = addForm.scopeGrants
          .filter((g) => !g.instanceIds || g.instanceIds.length === 0)
          .map((g) => g.tenantId);
        if (cascadeTenants.length > 0) payload.scopeTenantIds = cascadeTenants;
      }
      const res = await createConnector?.(payload);
      if (!res || res.ok === false) {
        setAddError(res?.error || res?.data?.error || `Create failed (HTTP ${res?.status || '?'}).`);
        return;
      }
      /* V227 — per-instance follow-up. The create endpoint only accepts
       * cascade tenants; any per-instance restrictions go via a follow-up
       * assignTenantGrants on the new id. */
      // The create endpoint returns the connector entity itself, not a
      // sharing envelope — the envelope only wraps the list/detail responses.
      const newId = res?.data?.id;
      const perInstanceGrants = (canGrantScope && Array.isArray(addForm.scopeGrants))
        ? addForm.scopeGrants.filter((g) => Array.isArray(g.instanceIds) && g.instanceIds.length > 0)
        : [];
      if (newId && perInstanceGrants.length > 0) {
        try {
          await api.assignConnectorTenantGrants(newId, perInstanceGrants);
        } catch (grantErr) {
          addToast({ tone: 'warning', title: 'Per-instance share partially failed',
            description: `Connector created, but per-instance grants didn't apply: ${grantErr?.message || String(grantErr)}. Use "Assign to tenants" on the row to retry.` });
        }
      }
      addToast({ tone: 'success', title: `Connector "${addForm.name}" created` });
      setAddOpen(false);
      if (typeof loadConnectors === 'function') loadConnectors();
    } catch (e) {
      setAddError(e?.message || String(e));
    } finally {
      setAddSaving(false);
    }
  };

  /* G162 — Catalogue (manifests + categories) is fetched once and held in
   * state; the installed-augmented marketplace is derived via useMemo from
   * `connectors` so it stays in sync as PlatformContext re-emits.
   *
   * Old code had two bugs:
   *  1) Stale closure — useEffect(reload, []) ran once with empty connectors
   *     and never re-ran when loadConnectors() finished.
   *  2) Argument order — findMarketplaceForInstalled signature is
   *     (marketplaceConnectorsArray, installedConnector) but V2 was calling
   *     it as (singleManifest, installedArray). Internal Array.isArray check
   *     on the first arg returned false → function returned null for every
   *     row → installed count was always 0.
   */
  const [registryManifests, setRegistryManifests]   = useState([]);
  const [registryCategories, setRegistryCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  /* Fetch catalogue once on mount + ensure connectors are loaded. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listConnectorManifests().catch(() => ({ ok: false, data: [] })),
      listConnectorCategories().catch(() => ({ ok: false, data: [] })),
    ]).then(([mfRes, catRes]) => {
      if (cancelled) return;
      const manifests = Array.isArray(mfRes?.data) ? mfRes.data
                     : Array.isArray(mfRes?.data?.items) ? mfRes.data.items
                     : Array.isArray(mfRes) ? mfRes : [];
      const categories = Array.isArray(catRes?.data) ? catRes.data
                      : Array.isArray(catRes?.data?.items) ? catRes.data.items
                      : Array.isArray(catRes) ? catRes : [];
      setRegistryManifests(manifests);
      setRegistryCategories(categories);
    }).finally(() => { if (!cancelled) setLoading(false); });
    if (typeof loadConnectors === 'function') loadConnectors();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  /* Build the registry-backed catalogue. */
  const catalogue = useMemo(() => {
    const built = buildMarketplaceFromRegistry(registryManifests, registryCategories) || { connectors: [] };
    return Array.isArray(built.connectors) ? built.connectors : [];
  }, [registryManifests, registryCategories]);

  /* Derive marketplace-with-installed from catalogue + connectors.
   * Iterate INSTALLED → resolve manifest (correct argument order, matches v1
   * ConnectorsPage). Build a manifest-id → instance map, then map across
   * the catalogue. */
  const marketplace = useMemo(() => {
    const installed = connectors || [];
    const installMap = new Map(); // manifestKey -> instance
    const matchedInstalledIds = new Set();
    for (const inst of installed) {
      const matched = findMarketplaceForInstalled(catalogue, inst);
      if (matched) {
        const key = matched.id || matched.manifestId || matched.name;
        if (key && !installMap.has(key)) {
          installMap.set(key, inst);
          matchedInstalledIds.add(inst.id);
        }
      }
    }
    const marketplaceWithInstalled = catalogue.map(m => {
      const key = m.id || m.manifestId || m.name;
      const inst = key ? installMap.get(key) : null;
      /* CONN-SCOPE — lift the ownership flags from the installed instance onto
       * the row so the row-action gates (Assign / Settings / Delete, which read
       * `r.__isOwner` / `r.__sharedWithGrants`) work. Previously only `instance`
       * was nested, so `r.__isOwner` was undefined for every catalogue-matched
       * installed row → `undefined !== false` left Assign visible and
       * `undefined === false` left Settings/Delete enabled even on shared-into
       * (non-owner) connectors. Custom rows already carry these via `...i`. */
      return inst
        ? { ...m, installed: true, instance: inst,
            __isOwner: inst.__isOwner,
            __sharedWithGrants: inst.__sharedWithGrants }
        : { ...m, installed: false, instance: null };
    });
    /* Custom installed connectors (no catalogue match) appear as their own rows. */
    const customInstalled = installed.filter(i => !matchedInstalledIds.has(i.id));
    return [
      ...marketplaceWithInstalled,
      ...customInstalled.map(i => ({
        ...i, installed: true, instance: i, isCustom: true,
        tier: i.tier || 'free', status: i.status || 'connected',
      })),
    ];
  }, [catalogue, connectors]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [mfRes, catRes] = await Promise.all([
        listConnectorManifests().catch(() => ({ ok: false, data: [] })),
        listConnectorCategories().catch(() => ({ ok: false, data: [] })),
      ]);
      const manifests = Array.isArray(mfRes?.data) ? mfRes.data
                     : Array.isArray(mfRes?.data?.items) ? mfRes.data.items
                     : Array.isArray(mfRes) ? mfRes : [];
      const categories = Array.isArray(catRes?.data) ? catRes.data
                      : Array.isArray(catRes?.data?.items) ? catRes.data.items
                      : Array.isArray(catRes) ? catRes : [];
      setRegistryManifests(manifests);
      setRegistryCategories(categories);
      if (typeof loadConnectors === 'function') await loadConnectors();
    } finally {
      setLoading(false);
    }
  }, [loadConnectors]);

  /* Facets — G131b: category is the primary marketplace dimension
   * (LLM Provider / SIEM / EDR / IAM / etc.). marketplaceFromRegistry
   * exposes both `category` (pretty name) and `marketplaceCategory`
   * (kebab id) — we facet on the pretty name. */
  const [facets, setFacets] = useState(() => ({
    category: new Set(),
    status:   new Set(),
    vendor:   new Set(),
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({ category: new Set(), status: new Set(), vendor: new Set() });

  /* Derive category + vendor options from data, sorted alphabetically. */
  const categories = useMemo(() => {
    const s = new Set();
    for (const c of marketplace) if (c.category) s.add(c.category);
    return Array.from(s).sort();
  }, [marketplace]);

  const vendors = useMemo(() => {
    const s = new Set();
    for (const c of marketplace) if (c.vendor) s.add(c.vendor);
    return Array.from(s).sort();
  }, [marketplace]);

  /* G173 — Facets: Category + Status (real health/activity) + Vendor.
   * Tier dropped completely (marketplace pricing, not a useful filter). */
  const FACET_GROUPS = useMemo(() => ([
    { key: 'category', label: 'Category',
      options: categories.map(c => ({ key: c, label: c })) },
    { key: 'status',   label: 'Status',
      options: STATUS_OPTIONS.map(s => ({ key: s.key, label: s.label })) },
    ...(vendors.length > 1 ? [{ key: 'vendor', label: 'Vendor',
      options: vendors.map(v => ({ key: v, label: v })) }] : []),
  ]), [categories, vendors]);

  const filtered = useMemo(() => {
    return marketplace.filter(c => {
      const stat = rowHealthState(c);
      if (facets.category.size > 0 && !facets.category.has(c.category)) return false;
      if (facets.status.size > 0 && !facets.status.has(stat)) return false;
      if (facets.vendor.size > 0 && !facets.vendor.has(c.vendor)) return false;
      return true;
    });
  }, [marketplace, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page after
   * narrowing the result set. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = marketplace.filter(c => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            if (og.key === 'category')      { if (!sel.has(c.category)) return false; }
            else if (og.key === 'vendor')   { if (!sel.has(c.vendor)) return false; }
            else if (og.key === 'status')   { if (!sel.has(rowHealthState(c))) return false; }
          }
          if (g.key === 'category') return c.category === opt.key;
          if (g.key === 'vendor')   return c.vendor === opt.key;
          if (g.key === 'status')   return rowHealthState(c) === opt.key;
          return false;
        }).length;
      }
    }
    return counts;
  }, [marketplace, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  /* Density */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('connectors-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('connectors-density', next); } catch {}
  };

  /* Slideout */
  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { closeOtherSlideouts('detail'); setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  /* RC-74.1 — Used-by state + lazy fetch.
   *
   * V1 showed every skill currently bound to this connector (with the
   * agent it lives on + an Unlink button) inside the detail modal. V2
   * dropped the whole view. We restore it as a tab that fetches on
   * demand: null = "not loaded yet", [] = "loaded, none", non-empty
   * = the linked-skill list (each entry includes the parent agentId
   * so the row can offer an Unlink that calls unlinkSkillConnector).
   *
   * Why lazy: most detail-view sessions don't open this tab, and the
   * endpoint hits the DB. Loading on tab activation keeps the slideout
   * snappy and avoids a redundant call on every row click. */
  const [usedBySkills, setUsedBySkills] = useState(null);
  const [usedByLoading, setUsedByLoading] = useState(false);
  const [usedByError, setUsedByError] = useState(null);

  useEffect(() => {
    // Reset when the operator opens a different connector — the prior
    // list is wrong for the new connector.
    setUsedBySkills(null);
    setUsedByError(null);
  }, [selected?.id]);

  useEffect(() => {
    if (slideoutTab !== 'usedby') return;
    if (!selected?.id) return;
    if (usedBySkills !== null) return;       // already loaded
    if (typeof api.getConnectorSkills !== 'function') {
      setUsedBySkills([]);
      return;
    }
    /* RC-74.1a — Use the install-time DB UUID, not the manifest id.
     * For not-installed connectors there's no instance, so there are
     * also no consumers — short-circuit to [] without a fetch that
     * would 400 on the BE's UUID parser. */
    if (!selected.installed) {
      setUsedBySkills([]);
      return;
    }
    const connectorUuid = selected.instance?.id || selected.id;
    let cancelled = false;
    setUsedByLoading(true);
    setUsedByError(null);
    api.getConnectorSkills(connectorUuid)
      .then(res => {
        if (cancelled) return;
        if (res && res.ok === false) {
          setUsedByError(res.error || 'Failed to load consumers');
          setUsedBySkills([]);
        } else {
          // Endpoint returns either an array or { skills: [...] } depending on BE shape.
          const list = Array.isArray(res?.data) ? res.data
                     : Array.isArray(res?.data?.skills) ? res.data.skills
                     : Array.isArray(res) ? res
                     : [];
          setUsedBySkills(list);
        }
      })
      .catch(err => {
        if (cancelled) return;
        setUsedByError(err?.message || String(err));
        setUsedBySkills([]);
      })
      .finally(() => { if (!cancelled) setUsedByLoading(false); });
    return () => { cancelled = true; };
  }, [slideoutTab, selected?.id, usedBySkills]);

  /* RC-74.4 — Row-level "Test connection" action.
   *
   * V1 puts Test on every installed card; V2 buried it in the config
   * modal (Settings → … → Test). This callback is the daily-driver
   * one-click path: probe + toast result. Idempotent and side-effect
   * free — Test never writes config, never rotates secrets. Defensive:
   * works whether the BE returns {ok:true, data:{success:bool}},
   * {ok:false, error:'...'}, or throws.
   *
   * The same logic also runs from the slideout header's Test button
   * (RC-74.4 surface 2) — duplicated callsite is intentional so the
   * two surfaces can be tweaked independently. */
  const testConnectorRow = useCallback(async (row) => {
    if (!row?.id || typeof api.testConnector !== 'function') return;
    /* RC-74.4a — Use the install-time DB UUID, not the manifest id.
     * Same defect that hit RC-74.1a: marketplace rows carry the
     * manifest id (string "anthropic") as `row.id`; the BE
     * /connectors/{id}/test endpoint expects a UUID and looks the
     * connector instance up by primary key. Passing the manifest id
     * routes to either a 400 (UUID parse) or a not-found, surfacing
     * as a generic "connection failed" toast even when the connector
     * is in fact Connected (status field set on the instance row).
     * The fallback to `row.id` keeps the V1 free-form-connector path
     * working — those rows ARE created with the UUID as their id and
     * have no `instance` sub-object. */
    if (!row.installed) {
      addToast({ tone: 'warning', title: `${row.name}: not installed`, description: 'Install this connector before testing.' });
      return;
    }
    const connectorUuid = row.instance?.id || row.id;
    addToast({ tone: 'info', title: `Testing ${row.name}…` });
    try {
      const res = await api.testConnector(connectorUuid);
      const ok = res && res.ok !== false && res.data?.success !== false;
      addToast({
        tone: ok ? 'success' : 'danger',
        title: ok ? `${row.name}: connection OK` : `${row.name}: connection failed`,
        description: res?.data?.message || res?.error || (ok ? 'Probe returned success.' : 'See BE logs for details.'),
      });
    } catch (e) {
      addToast({ tone: 'danger', title: `${row.name}: connection failed`, description: e?.message || String(e) });
    } finally {
      /* RC-74.11 — Test Connection is NOT read-only on the server: ConnectorService
       * .testConnection writes status ("connected"/"error"), lastTestResult and lastSyncedAt to
       * the connector row. Without this reload the page keeps the pre-test instance object, so a
       * connector the backend now records as connected/SUCCESS still renders the "Inactive" pill
       * with a "No test result" tooltip and leaves the CONNECTED tile on zero — a green success
       * toast sitting directly above a row that contradicts it. The operator is then choosing
       * between two of our own statements with no way to tell which is stale.
       *
       * In `finally` deliberately: a FAILED test writes status "error", so the row needs
       * refreshing on that path too, not only on success. */
      if (typeof loadConnectors === 'function') loadConnectors();
    }
  }, [addToast, loadConnectors]);

  const unlinkUsedByRow = useCallback(async (row) => {
    if (!selected?.id || !row?.agentId || !row?.id) return;
    if (typeof api.unlinkSkillConnector !== 'function') return;
    // RC-74.1a — same UUID rationale as the load effect above.
    const connectorUuid = selected.instance?.id || selected.id;
    const res = await api.unlinkSkillConnector(row.agentId, row.id, connectorUuid);
    if (res && res.ok === false) {
      addToast({ tone: 'danger', title: 'Unlink failed', description: res?.error || 'Backend rejected unlink' });
      return;
    }
    addToast({ tone: 'success', title: 'Unlinked', description: `Removed ${row.name || row.id} from this connector.` });
    // Force a re-fetch on next tab open by clearing the cache.
    setUsedBySkills((prev) => (prev || []).filter(r => r.id !== row.id));
  }, [selected?.id, addToast]);

  /* G164 — V2 install + configure modals (no more legacy=1 punt) */
  const [installTarget, setInstallTarget] = useState(null);   // manifest row
  const [configTarget,  setConfigTarget]  = useState(null);   // { connector, manifest }
  const [deleteTarget,  setDeleteTarget]  = useState(null);   // installed connector

  /* Single-slideout invariant: only ONE slideout may be open at a time. Each open-*
   * handler closes the others first, so opening (e.g.) Configure from inside the detail
   * slideout transitions to it rather than stacking a second slideout on top. */
  const closeOtherSlideouts = (keep) => {
    if (keep !== 'detail')    setSelected(null);
    if (keep !== 'install')   setInstallTarget(null);
    if (keep !== 'configure') setConfigTarget(null);
  };
  const openInstall = (manifestRow) => { closeOtherSlideouts('install'); setInstallTarget(manifestRow); };
  const openConfigure = (manifestRow) => {
    const conn = manifestRow.instance || (manifestRow.installed ? manifestRow : null);
    if (!conn) return;
    closeOtherSlideouts('configure');
    setConfigTarget({ connector: conn, manifest: manifestRow });
  };
  const openDelete = (manifestRow) => {
    const conn = manifestRow.instance || (manifestRow.installed ? manifestRow : null);
    if (!conn) return;
    setDeleteTarget(conn);
  };

  const handleInstalled = (createdConnector) => {
    /* Find the matching manifest so the Configure modal renders the right schema. */
    const matched = installTarget;
    setInstallTarget(null);
    if (typeof loadConnectors === 'function') loadConnectors();
    if (createdConnector?.id) {
      addToast(`"${createdConnector.name}" installed — configure credentials next.`, 'success');
      setConfigTarget({ connector: createdConnector, manifest: matched });
    } else {
      addToast('Connector installed.', 'success');
    }
  };

  const handleConfigSaved = () => {
    if (typeof loadConnectors === 'function') loadConnectors();
    addToast('Configuration saved.', 'success');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) { setDeleteTarget(null); return; }
    const id = deleteTarget.id;
    const name = deleteTarget.name;
    try {
      const result = await deleteConnector(id);
      if (result && result.ok !== false) {
        addToast(`Connector "${name}" deleted.`, 'success');
        if (typeof loadConnectors === 'function') loadConnectors();
        if (selected?.instance?.id === id || selected?.id === id) setSelected(null);
      } else {
        addToast(`Delete failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (e) {
      addToast(`Delete failed: ${e?.message || 'Network error'}`, 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  /* KPIs */
  /* G173 — KPIs use the unified rowHealthState helper so they always match
   * the row Pill + facet counts. No more divergence between header KPI and
   * what users see in the table. */
  const kpis = useMemo(() => {
    const states = marketplace.map(c => rowHealthState(c));
    return {
      available: marketplace.length,
      installed: marketplace.filter(c => c.installed).length,
      connected: states.filter(s => s === 'connected').length,
      pending:   states.filter(s => s === 'pending').length,
      error:     states.filter(s => s === 'error').length,
      inactive:  states.filter(s => s === 'inactive' || s === 'disabled').length,
    };
  }, [marketplace]);

  /* RC-74.7 — Stats breakdowns (By Category / By Vendor / By Status).
   *
   * V1 had a dedicated Dashboard tab; V2 collapsed it to a 4-card KPI
   * strip which loses the breakdown shape (which categories dominate?
   * which vendors? what's the health distribution?). We surface the
   * same breakdowns inside a single collapsed `<details>` block below
   * the KPI strip — operators who don't care never see it; operators
   * who do get the V1 view back in one click. Computed once per
   * marketplace render; cheap because the table is in-memory. */
  const statsBreakdowns = useMemo(() => {
    const tally = (key) => {
      const m = new Map();
      for (const c of marketplace) {
        const v = c?.[key] || '—';
        m.set(v, (m.get(v) || 0) + 1);
      }
      // Largest first; cap at 10 so the panel doesn't get unwieldy.
      return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
    };
    const tallyStatus = () => {
      const m = new Map();
      for (const c of marketplace) {
        const s = rowHealthState(c);
        m.set(s, (m.get(s) || 0) + 1);
      }
      return [...m.entries()].sort((a, b) => b[1] - a[1]);
    };
    return {
      byCategory: tally('category'),
      byVendor:   tally('vendor'),
      byStatus:   tallyStatus(),
    };
  }, [marketplace]);

  return (
    <div className="min-h-screen bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Connectors</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {kpis.available} available · {kpis.installed} installed · {kpis.connected} connected · {filtered.length} match filters
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity} title={`Row density: ${density}`}>
            Density: {density.charAt(0).toUpperCase() + density.slice(1)}
          </Button>
          <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
            onClick={openAdd}>
            Add Connector
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Available"  value={kpis.available} tone="cyan" />
        <MetricCard label="Installed"  value={kpis.installed} tone="action" />
        <MetricCard label="Connected"  value={kpis.connected} tone="success" />
        <MetricCard label="Pending auth" value={kpis.pending} tone="warning" />
      </div>

      {/* RC-74.7 — V1 stats dashboard restored as collapsed expandable.
          Operators who want category/vendor/status breakdown click to
          expand; the default view stays clean. Three columns map to
          V1's three breakdown tables. */}
      {kpis.available > 0 && (
        <details className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 group">
          <summary className="cursor-pointer text-[11.5px] text-v2-ink-secondary font-medium select-none flex items-center justify-between">
            <span>Show inventory breakdown — by category, vendor, status</span>
            <span className="text-[10.5px] text-v2-ink-muted group-open:hidden">▾</span>
            <span className="text-[10.5px] text-v2-ink-muted hidden group-open:inline">▴</span>
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1.5">By category</div>
              <ul className="space-y-0.5 text-[12px]">
                {statsBreakdowns.byCategory.map(([k, v]) => (
                  <li key={k} className="flex items-baseline justify-between gap-2 border-b border-v2-border-subtle/50 py-0.5">
                    <span className="text-v2-ink truncate">{k}</span>
                    <span className="text-v2-ink-muted tabular-nums">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1.5">By vendor</div>
              <ul className="space-y-0.5 text-[12px]">
                {statsBreakdowns.byVendor.map(([k, v]) => (
                  <li key={k} className="flex items-baseline justify-between gap-2 border-b border-v2-border-subtle/50 py-0.5">
                    <span className="text-v2-ink truncate">{k}</span>
                    <span className="text-v2-ink-muted tabular-nums">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1.5">By status</div>
              <ul className="space-y-0.5 text-[12px]">
                {statsBreakdowns.byStatus.map(([k, v]) => (
                  <li key={k} className="flex items-baseline justify-between gap-2 border-b border-v2-border-subtle/50 py-0.5">
                    <span className="text-v2-ink truncate capitalize">{statusLabelFor(k)}</span>
                    <span className="text-v2-ink-muted tabular-nums">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </details>
      )}

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 items-start grid-cols-1 lg:grid-cols-[240px_1fr]" style={{ minHeight: '420px' }}>
        <FacetRail
          groups={facetGroupsWithCounts}
          onToggle={toggleFacet}
          ariaLabel="Connector filters"
        />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Plug size={20} />}
            title="No connectors match"
            description={activeFilterChips.length > 0
              ? 'No connectors match the current filters. Adjust filters or clear them.'
              : 'No connectors available — try adding one or seeding the marketplace.'}
            primaryAction={activeFilterChips.length > 0
              ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
              : <Button variant="primary" iconLeft={<Plus />} onClick={openAdd}>Add Connector</Button>}
          />
        ) : (
          <Table
            ariaLabel="Connectors"
            density={density}
            columns={[
              { key: 'name', header: 'Connector', sortable: true, render: (r) => (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-v2-md flex items-center justify-center font-bold text-[12px] bg-v2-cyan-soft text-v2-cyan">
                    {r.icon || (r.name || '??').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-v2-ink">{r.name}</div>
                    <div className="text-[10.5px] text-v2-ink-muted">{r.description?.slice(0, 60) || r.vendor || '—'}</div>
                  </div>
                </div>
              )},
              { key: 'category', header: 'Category', render: (r) => (
                <Pill tone="cyan" size="sm">{r.category || '—'}</Pill>
              )},
              { key: 'vendor', header: 'Vendor', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{r.vendor || '—'}</span>
              )},
              { key: 'auth', header: 'Auth', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{r.authType || r.authMethod || '—'}</span>
              )},
              { key: 'status', header: 'Status', render: (r) => {
                /* G173 — real health/activity Pill; matches V1 display. */
                const s = rowHealthState(r);
                const inst = r.instance || r;
                const tip = inst.lastTestResult ? `Last test: ${inst.lastTestResult}` : (r.installed ? 'No test result' : 'Not installed');
                return (
                  <span title={tip}>
                    <Pill tone={STATUS_TONE[s] || 'muted'} size="sm" dot={s !== 'available'}>{statusLabelFor(s)}</Pill>
                  </span>
                );
              }},
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {r.installed ? (
                    <>
                      {/* RC-74.4 — Test connection row action.
                          Single-click probe with toast result; mirrors
                          V1's per-card Test button. Read-only — never
                          mutates config or rotates secrets. Available
                          regardless of __isOwner because a non-owner
                          tenant still needs to know whether the connector
                          shared into their scope is reachable. */}
                      <Button variant="ghost" size="sm"
                        onClick={() => testConnectorRow(r)}
                        title="Test connection">
                        <Zap size={13} />
                      </Button>
                      {/* V226 / CONN-SCOPE — only owner + grant-capable role
                        * sees the Assign-to-tenants action. Shared-into rows
                        * are read-only on this surface. */}
                      {canGrantScope && r.__isOwner !== false && (
                        <Button variant="ghost" size="sm"
                          onClick={() => setAssignTenantsRow(r)}
                          title="Assign to tenants">
                          <Users size={13} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm"
                        onClick={() => openConfigure(r)}
                        title={r.__isOwner === false ? 'Owner tenant only' : 'Configure'}
                        disabled={r.__isOwner === false}>
                        <Settings size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                        onClick={() => openDelete(r)}
                        title={r.__isOwner === false ? 'Owner tenant only' : 'Delete'}
                        disabled={r.__isOwner === false}>
                        <Trash2 size={13} />
                      </Button>
                    </>
                  ) : (
                    <Button variant="primary" size="sm"
                      onClick={() => openInstall(r)}>
                      Install
                    </Button>
                  )}
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id || r.value || r.name}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {/* G175 — Pagination control mirrors filtered count, not full marketplace. */}
      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="connectors"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Connector detail'}
        /* RC-74.8 — Widened from 520→720 to fit 8 tabs on a single
           line. RC-74 added Guardrails / Evals / Used by, taking the
           strip from 5 tabs to 8; at the prior 520px width "Used by"
           wrapped to a second line and "Audit" clipped. 720 matches
           the Configure modal so the two surfaces feel coherent. */
        width={720}
        /* RC-74 — Tab strip extended with three V1-parity restorations:
            · Guardrails / Evals — governance signal dropped in the V2
              redesign (same gap RC-71 fixed for agents).
            · Used by — answers "who is consuming this connector?"; was
              V1's "Linked Skills" panel with unlink actions.
            Counts collapse with `|| undefined` so a connector with no
            guardrails/evals/consumers doesn't render an empty badge.
            The `__usedByCount` field is populated by the fetch effect
            below — null until the operator opens the tab, then a
            number. */
        tabs={[
          { id: 'overview',   label: 'Overview' },
          { id: 'auth',       label: 'Auth' },
          { id: 'skills',     label: 'Skills',
            badge: (selected?.skills || []).length || undefined },
          { id: 'guardrails', label: 'Guardrails',
            badge: (selected?.guardrails || []).length || undefined },
          { id: 'evals',      label: 'Evals',
            badge: (selected?.evals || []).length || undefined },
          { id: 'usedby',     label: 'Used by',
            badge: typeof usedBySkills?.length === 'number' ? (usedBySkills.length || undefined) : undefined },
          { id: 'manifest',   label: 'Manifest' },
          { id: 'audit',      label: 'Audit' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              {(() => {
                const s = rowHealthState(selected);
                return s === 'available'
                  ? <Pill tone="muted" size="sm">Available</Pill>
                  : <Pill tone={STATUS_TONE[s] || 'muted'} size="sm" dot>{statusLabelFor(s)}</Pill>;
              })()}
              {selected.tier && (
                <span className="text-[10.5px] text-v2-ink-muted">{selected.tier}</span>
              )}
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">
              {selected.vendor && <span className="text-v2-ink">{selected.vendor}</span>}
              {selected.vendor && selected.category && <span className="text-v2-ink-muted"> · </span>}
              {selected.category}
            </p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {selected.installed ? (
                <Button variant="primary" size="sm" iconLeft={<Settings size={13} />}
                  onClick={() => openConfigure(selected)}>
                  Configure
                </Button>
              ) : (
                <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
                  onClick={() => openInstall(selected)}>
                  Install
                </Button>
              )}
              {/* RC-74.2 — OAuth "Connect my account" surface.
                  V1 prominently exposed the OAuth user-grant button for
                  authorization_code-grant connectors (Slack, Salesforce,
                  Google, etc.) — this is the per-user authorization step
                  that completes installation; without it the connector
                  is installed but inert. V2 buried it inside the config
                  modal where the operator wasn't sure how to find it.
                  We restore the explicit CTA next to Configure / Install
                  so operators can click straight through to the OAuth
                  provider. The button only renders when:
                    · the connector is installed AND
                    · its authType is oauth2 (any grant type that produces
                      a user redirect is fine — the BE decides whether the
                      authorize call needs a redirect or completes inline).
                  Calls api.authorizeConnector which the BE turns into a
                  302 to the OAuth provider's /authorize endpoint.
                  Defensive: failures surface as a toast rather than
                  blocking the whole header. */}
              {selected.installed
                && /^oauth/i.test(String(selected.authType || selected.authMethod || ''))
                && typeof api.authorizeConnector === 'function' && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<ExternalLink size={13} />}
                  onClick={async () => {
                    try {
                      const res = await api.authorizeConnector(selected.id);
                      if (res && res.ok === false) {
                        addToast({ tone: 'danger', title: 'OAuth start failed', description: res?.error || 'Backend rejected authorize call' });
                        return;
                      }
                      // BE may return a URL to navigate to (authorization_code grant)
                      // or a success payload (client_credentials / already authorized).
                      const url = res?.data?.url || res?.data?.authorizeUrl || res?.url;
                      if (url) {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        addToast({ tone: 'success', title: 'Authorized', description: 'Connector is now authorized.' });
                      }
                    } catch (e) {
                      addToast({ tone: 'danger', title: 'OAuth error', description: e?.message || String(e) });
                    }
                  }}
                >
                  Connect my account
                </Button>
              )}
              {/* RC-74.4 — Test connection in the slideout header.
                  Mirrors the row-level Test action so an operator who's
                  drilled into the detail can probe without bouncing
                  back to the row. RC-74.4a — both surfaces now share a
                  single helper (testConnectorRow) so the manifest-id
                  → instance-UUID rewrite happens in exactly one place. */}
              {selected.installed && typeof api.testConnector === 'function' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => testConnectorRow(selected)}
                >
                  Test
                </Button>
              )}
              {selected.docsUrl && (
                <Button variant="ghost" size="sm" iconLeft={<ExternalLink size={13} />}
                  onClick={() => window.open(selected.docsUrl, '_blank')}>
                  Docs
                </Button>
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Overview" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink leading-relaxed mb-3">
                {selected.longDescription || selected.description || 'No description available.'}
              </p>
              <dl className="grid grid-cols-2 gap-3 text-[12px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Category</dt><dd className="text-v2-ink mt-0.5">{selected.category || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Vendor</dt><dd className="text-v2-ink mt-0.5">{selected.vendor || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Auth method</dt><dd className="text-v2-ink mt-0.5">{selected.authType || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Tier</dt><dd className="text-v2-ink mt-0.5">{selected.tier || 'free'}</dd></div>
                <div className="col-span-2"><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Manifest ID</dt><dd className="text-v2-ink mt-0.5 font-mono-v2 text-[11px]">{selected.manifestId || selected.id || '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'auth' && (
          <Card padding="sm">
            <Card.Header title="Authentication" actions={
              selected.installed ? (
                <Button variant="primary" size="sm" iconLeft={<Settings size={13} />}
                  onClick={() => openConfigure(selected)}>
                  Configure
                </Button>
              ) : null
            } />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink leading-relaxed">
                Auth method: <strong>{selected.authType || 'none'}</strong>
              </p>
              {(selected.authFields || []).length > 0 && (
                <>
                  <h4 className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold mt-3 mb-1.5">Required fields</h4>
                  <ul className="space-y-1 text-[12px]">
                    {(selected.authFields || []).map((f, i) => (
                      <li key={i} className="font-mono-v2 text-v2-ink">{typeof f === 'string' ? f : (f.name || JSON.stringify(f))}</li>
                    ))}
                  </ul>
                </>
              )}
              {selected.installed && selected.instance && (
                <p className="text-[12px] text-v2-ink-muted mt-3">
                  Last authorized: {selected.instance.lastAuthorized ? new Date(selected.instance.lastAuthorized).toLocaleString() : 'never'}
                </p>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'skills' && (
          <Card padding="sm">
            <Card.Header title={`Skills offered · ${(selected.skills || []).length}`} />
            <Card.Body>
              {(selected.skills || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No skills declared in the manifest.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {(selected.skills || []).map((s, i) => (
                    <li key={s.id || i} className="border-l-2 border-v2-border-subtle pl-3">
                      <div className="font-semibold text-v2-ink">{s.name || s.id}</div>
                      <div className="text-[11.5px] text-v2-ink-muted">{s.description || '—'}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}

        {/* RC-74.3 — Guardrails tab.
            Pre-RC-74 the connector marketplace dropped guardrails
            visibility — operators couldn't see what the connector
            promises NOT to do before installing. Same governance
            argument as RC-71 for agents. The fixture mapping in
            marketplaceFromRegistry.js already pulls these through
            (line 269, 357), so this is a pure FE surfacing change. */}
        {selected && slideoutTab === 'guardrails' && (
          <Card padding="sm">
            <Card.Header title={`Enforced guardrails · ${(selected.guardrails || []).length}`} />
            <Card.Body>
              {(selected.guardrails || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No guardrails declared in the manifest.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {selected.guardrails.map((g, i) => (
                    <li key={g.id || i} className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <SeverityBadge severity={g.severity} />
                        {g.id && <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted">{g.id}</span>}
                      </div>
                      <p className="text-[12.5px] text-v2-ink leading-relaxed">{g.rule || g.description || g.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}

        {/* RC-74.3 — Evals tab.
            Same restoration: the manifest's eval suite is the connector's
            quality contract ("Auth probe must succeed within 5s",
            "Throttling test must respect 429 backoff"). V1 surfaced it;
            V2 dropped it. */}
        {selected && slideoutTab === 'evals' && (
          <Card padding="sm">
            <Card.Header title={`Evaluation tests · ${(selected.evals || []).length}`} />
            <Card.Body>
              {(selected.evals || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No evaluation tests declared in the manifest.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {selected.evals.map((ev, i) => (
                    <li key={ev.name || i} className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-2.5">
                      <div className="flex items-center gap-2 mb-1">
                        <EvalTypeBadge type={ev.type} />
                        <span className="font-semibold text-v2-ink">{ev.name}</span>
                      </div>
                      {ev.description && (
                        <p className="text-[12px] text-v2-ink-secondary leading-relaxed">{ev.description}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}

        {/* RC-74.1 — Used by tab.
            "Which skills are bound to this connector right now?" — the
            single most important question before edit or delete. V1
            surfaced this inside the detail modal with an Unlink action
            per row; V2 dropped it.

            States:
              · usedBySkills === null     → not loaded yet (fetch fires)
              · usedByLoading             → spinner placeholder
              · usedByError               → error banner with retry hint
              · usedBySkills.length === 0 → "Nothing uses this yet."
              · otherwise                 → row list with Unlink action

            Each row shows the skill name + the agent it lives on (when
            the BE includes agentId) + an Unlink button that calls
            unlinkSkillConnector via unlinkUsedByRow. The row is also
            optimistically removed from the local list so the operator
            sees immediate feedback. */}
        {selected && slideoutTab === 'usedby' && (
          <Card padding="sm">
            <Card.Header title={
              usedBySkills === null
                ? 'Used by'
                : `Used by · ${usedBySkills.length} skill${usedBySkills.length === 1 ? '' : 's'}`
            } />
            <Card.Body>
              {usedByLoading && (
                <p className="text-v2-ink-muted text-[12.5px]">Loading consumers…</p>
              )}
              {!usedByLoading && usedByError && (
                <div className="bg-v2-danger-soft border border-v2-danger/30 text-v2-danger rounded-v2-sm px-2.5 py-1.5 text-[12px]">
                  Couldn't load consumers: {usedByError}
                </div>
              )}
              {!usedByLoading && !usedByError && usedBySkills && usedBySkills.length === 0 && (
                <p className="text-v2-ink-muted text-[12.5px]">
                  Nothing is using this connector yet. As soon as a skill links to it (from the agent's Skill Center),
                  it will show up here.
                </p>
              )}
              {!usedByLoading && !usedByError && usedBySkills && usedBySkills.length > 0 && (
                <ul className="space-y-1.5 text-[12.5px]">
                  {usedBySkills.map((row, i) => (
                    <li key={row.id || i} className="flex items-center gap-2 bg-v2-surface border border-v2-border-subtle rounded-v2-md px-2.5 py-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-v2-ink truncate">{row.name || row.skillName || row.id}</div>
                        <div className="text-[10.5px] text-v2-ink-muted truncate">
                          {/* agentName is best-effort — BE may return only agentId; either is useful context for the unlink decision. */}
                          {row.agentName ? `on ${row.agentName}` : (row.agentId ? `agent ${row.agentId}` : 'no agent context')}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="!text-v2-danger hover:!bg-v2-danger-soft"
                        onClick={() => unlinkUsedByRow(row)}
                        disabled={!row.agentId}
                        title={row.agentId ? 'Unlink this connector from the skill' : 'Cannot unlink — no agent context returned'}
                      >
                        Unlink
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}

        {selected && slideoutTab === 'manifest' && (
          <Card padding="sm">
            {/* G367 — Was a /connectors?legacy=1 bounce. The manifest
                is shown verbatim below; no extra V1 trip needed. */}
            <Card.Header title="Manifest" />
            <Card.Body>
              <pre className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted rounded-v2-sm p-3 max-h-[400px] overflow-auto">
                {JSON.stringify({
                  manifestId: selected.manifestId || selected.id,
                  name: selected.name,
                  category: selected.category,
                  marketplaceCategory: selected.marketplaceCategory,
                  vendor: selected.vendor,
                  version: selected.version,
                  tier: selected.tier,
                  authType: selected.authType,
                  authFields: selected.authFields,
                  baseUrl: selected.baseUrl,
                  skills: (selected.skills || []).map(s => s.id || s.name),
                  tags: selected.tags,
                }, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'audit' && (
          <Card padding="sm">
            <Card.Header title="Recent activity" />
            <Card.Body>
              {selected.installed && selected.instance ? (
                <ul className="space-y-2 text-[12.5px]">
                  {selected.instance.createdAt && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">
                        {new Date(selected.instance.createdAt).toLocaleDateString()}
                      </span>
                      <span>Installed</span>
                    </li>
                  )}
                  {selected.instance.lastAuthorized && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">
                        {new Date(selected.instance.lastAuthorized).toLocaleDateString()}
                      </span>
                      <span>Authorized</span>
                    </li>
                  )}
                </ul>
              ) : (
                <p className="text-v2-ink-muted text-[12.5px]">Not installed — no audit trail yet.</p>
              )}
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G164 — V2 install + configure flow (no more legacy=1 punt) */}
      <ConnectorInstallModalV2
        isOpen={!!installTarget}
        manifest={installTarget}
        tenantId={tenantId}
        orgId={orgId}
        onClose={() => setInstallTarget(null)}
        onInstalled={handleInstalled}
      />

      <ConnectorConfigModalV2
        isOpen={!!configTarget}
        connector={configTarget?.connector}
        manifest={configTarget?.manifest}
        onClose={() => {
          setConfigTarget(null);
          /* RC-74.11 — the Configure slideout's own Test Connection button writes the same
           * status/lastTestResult fields, and Save writes name, description and URL. Neither
           * refreshed the list, so closing the slideout returned the operator to a row still
           * showing what was true before they opened it. */
          if (typeof loadConnectors === 'function') loadConnectors();
        }}
        onSaved={handleConfigSaved}
      />

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title="Delete connector?"
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDelete}>Delete</Button>
          </>
        }
      >
        <p className="text-[12.5px] text-v2-ink leading-relaxed">
          Permanently delete <strong>{deleteTarget?.name}</strong>? Connected skills will be unlinked.
          This cannot be undone.
        </p>
      </Modal>

      {/* Phase 5B — Add Connector (free-form): Modal → Slideout per
        * G378. Form has 5-9 fields (name/desc/type/auth/url plus
        * auth-method-specific subset), substantial enough for a
        * slideout. */}
      <Slideout
        isOpen={addOpen}
        onClose={addSaving ? undefined : () => setAddOpen(false)}
        width={640}
        ariaLabel="Add Connector"
        header={
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone="cyan" size="sm">Connector</Pill>
            </div>
            <h2 className="text-[16px] font-bold m-0">Add Connector</h2>
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2 w-full">
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
            <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
              onClick={submitAdd} disabled={addSaving || !addForm.name.trim() || !addForm.url.trim()}>
              {addSaving ? 'Creating…' : 'Create connector'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3.5">
          <p className="text-[12px] text-v2-ink-secondary">
            Add a free-form connector — useful for internal REST APIs, custom integrations, or services.
            For pre-packaged connectors, install from the list of Prove7 certified connectors.
          </p>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Name *</span>
            <input
              type="text"
              className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
              value={addForm.name}
              onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))}
              disabled={addSaving}
              autoFocus
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Description</span>
            <input
              type="text"
              className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
              value={addForm.description}
              onChange={(e) => setAddForm(f => ({ ...f, description: e.target.value }))}
              disabled={addSaving}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Type</span>
              <select
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                value={addForm.type}
                onChange={(e) => setAddForm(f => ({ ...f, type: e.target.value }))}
                disabled={addSaving}
              >
                <option value="REST">REST</option>
                <option value="WEBHOOK">Webhook</option>
                <option value="GRAPHQL">GraphQL</option>
                <option value="SOAP">SOAP</option>
                <option value="DATABASE">Database</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth method</span>
              <select
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                value={addForm.authMethod}
                onChange={(e) => setAddForm(f => ({ ...f, authMethod: e.target.value }))}
                disabled={addSaving}
              >
                <option value="none">None</option>
                <option value="apiKey">API Key</option>
                <option value="bearer">Bearer Token</option>
                <option value="basic">Basic Auth</option>
                <option value="oauth2">OAuth 2.0</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">URL *</span>
            <input
              type="text"
              className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
              value={addForm.url}
              onChange={(e) => { setAddForm(f => ({ ...f, url: e.target.value })); setUrlTestResult(null); }}
              disabled={addSaving}
              placeholder="https://api.example.com/v1"
            />
            {/* RC-74.6 — Test URL probe.
                V1's Add Connector form has a "Test URL" probe that
                ping-checks the URL before save so the operator catches
                typos / DNS issues / unreachable hosts before committing
                a new connector row. V2's Add slideout dropped it. We
                restore it here as a secondary button anchored to the
                URL field. State is local (urlTestResult / urlTesting)
                because it's only meaningful while this form is open;
                resetting on every URL edit prevents stale green ticks
                from confusing the operator after they change the URL. */}
            <div className="mt-1.5 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (!addForm.url.trim()) return;
                  setUrlTesting(true);
                  setUrlTestResult(null);
                  try {
                    const res = await api.testConnectorUrl(addForm.url.trim());
                    const ok = res && res.ok !== false && res.data?.success !== false;
                    setUrlTestResult({
                      ok,
                      latencyMs: res?.data?.latencyMs,
                      status: res?.data?.status,
                      error: res?.error || res?.data?.error,
                    });
                  } catch (e) {
                    setUrlTestResult({ ok: false, error: e?.message || String(e) });
                  } finally {
                    setUrlTesting(false);
                  }
                }}
                disabled={addSaving || urlTesting || !addForm.url.trim() || typeof api.testConnectorUrl !== 'function'}
              >
                {urlTesting ? 'Probing…' : 'Test URL'}
              </Button>
              {urlTestResult && (
                <span className={`text-[11px] ${urlTestResult.ok ? 'text-v2-success' : 'text-v2-danger'}`}>
                  {urlTestResult.ok
                    ? `Reachable${urlTestResult.latencyMs != null ? ` · ${urlTestResult.latencyMs}ms` : ''}${urlTestResult.status ? ` · ${urlTestResult.status}` : ''}`
                    : `Unreachable${urlTestResult.error ? ` · ${urlTestResult.error.slice(0, 80)}` : ''}`}
                </span>
              )}
            </div>
          </label>
          {addForm.authMethod === 'apiKey' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth header</span>
                <input
                  type="text"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                  value={addForm.authHeader}
                  onChange={(e) => setAddForm(f => ({ ...f, authHeader: e.target.value }))}
                  disabled={addSaving}
                  placeholder="X-Api-Key"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">API key (will be encrypted)</span>
                <input
                  type="password"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                  value={addForm.apiKey}
                  onChange={(e) => setAddForm(f => ({ ...f, apiKey: e.target.value }))}
                  disabled={addSaving}
                  placeholder="sk-…"
                />
              </label>
            </div>
          )}
          {addForm.authMethod === 'bearer' && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Bearer token (will be encrypted)</span>
              <input
                type="password"
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                value={addForm.apiKey}
                onChange={(e) => setAddForm(f => ({ ...f, apiKey: e.target.value }))}
                disabled={addSaving}
              />
            </label>
          )}
          {/* CONN-OWNER — owner Org + Tenant picker. Required. */}
          <div className="pt-3 mt-2 border-t border-v2-border-subtle">
            <ConnectorOwnerPicker
              value={{ ownerOrgId: addForm.ownerOrgId, ownerTenantId: addForm.ownerTenantId }}
              onChange={({ ownerOrgId: nextOrg, ownerTenantId: nextTenant }) => setAddForm(f => ({
                ...f,
                ownerOrgId: nextOrg || '',
                ownerTenantId: nextTenant || '',
                /* Reset cross-tenant grants if owner-org changes — sibling
                 * tenants from the old org are no longer the right peer set. */
                scopeGrants: nextOrg !== f.ownerOrgId ? [] : f.scopeGrants,
              }))}
              sessionRole={auth?.session?.role}
              sessionOrgId={orgId}
              sessionTenantId={tenantId}
              readOnly={addSaving}
            />
          </div>
          {/* V226 / CONN-SCOPE — tenant scope picker. Org/Super/Platform
            * Admin only. Now scoped to the explicit OWNER org (from the
            * ConnectorOwnerPicker above), not session.orgId. */}
          {canGrantScope && addForm.ownerOrgId && (
            <div className="pt-3 mt-2 border-t border-v2-border-subtle">
              <TenantScopePicker
                value={addForm.scopeGrants}
                onChange={(grants) => setAddForm(f => ({ ...f, scopeGrants: grants }))}
                ownerTenantId={addForm.ownerTenantId}
                orgId={addForm.ownerOrgId}
                ariaLabel="Cross-tenant share scope for new connector"
              />
            </div>
          )}
          {addError && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
              {addError}
            </div>
          )}
        </div>
      </Slideout>

      {/* V226 / CONN-SCOPE — row-level Assign-to-tenants slideout. Opens
        * from the Users icon in the row actions. Targets the generic
        * connector endpoints. */}
      <ConnectorAssignTenantsSlideout
        isOpen={!!assignTenantsRow}
        onClose={() => setAssignTenantsRow(null)}
        onSaved={() => {
          setAssignTenantsRow(null);
          if (typeof loadConnectors === 'function') loadConnectors();
        }}
        connector={assignTenantsRow}
        /* RC-75 — Explicit instance UUID. assignTenantsRow is the
         * marketplace row whose .id is the manifest string ("anthropic");
         * the BE assign/revoke endpoints need the install-time DB UUID
         * (selected.instance.id). Same shape fix that landed in RC-74.1a
         * for "Used by" and RC-74.4a for "Test connection". The row icon
         * is gated by `r.__isOwner !== false` further up so we always
         * have an `instance` here when this slideout opens. */
        connectorId={assignTenantsRow?.instance?.id || assignTenantsRow?.id}
        /* V227 — pass the rich grant shape so per-instance restrictions
         * round-trip through the picker. */
        currentScope={assignTenantsRow?.__sharedWithGrants || []}
        orgId={orgId}
        apiAssignGrants={api.assignConnectorTenantGrants}
        apiRevokeTenant={api.revokeConnectorTenantScope}
        resourceLabel="Connector"
      />
    </div>
  );
}

export default function ResolvedConnectorsPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthBuildSectionEnabled()) return <ConnectorsPage {...props} />;
  return <ConnectorsPageV2Inner {...props} />;
}

export { ConnectorsPageV2Inner };
